import React, { useState, useEffect, useCallback, useRef } from 'react';
import VRMAvatar from './VRMAvatar';
import ConversationInput from './ConversationInput';
import TextDisplay from './TextDisplay';
import ControlPanel from './ControlPanel';
import TemperatureControl from './TemperatureControl';
import PronunciationTester from './PronunciationTester';
import './PronunciationTester.css';
import { ElevenLabsService } from '../services/elevenLabs';
import { analyticsService } from '../services/analytics';
import { SpeechRecognition } from '../types/speech';


interface WelcomeAvatarProps {
  apiKey: string;
  agentId?: string;
  welcomeMessage?: string;
  voiceId?: string;
  autoPlay?: boolean;
  onStartPoc?: () => void;
  onStop?: () => void;
  pocStarted?: boolean;
}

const WelcomeAvatar: React.FC<WelcomeAvatarProps> = ({
  apiKey,
  agentId,
  welcomeMessage = "Hello! Welcome to our application. I'm your virtual assistant, and I'm here to help you with anything you need. How can I assist you today?",
  voiceId,
  autoPlay = true,
  onStartPoc,
  onStop,
  pocStarted = false,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [avatarLoaded, setAvatarLoaded] = useState(false);
  const [hasInitialized, setHasInitialized] = useState(false);
  const [currentText, setCurrentText] = useState('');
  const [displayedText, setDisplayedText] = useState(''); // Text to show in the UI (persists across responses)
  const [textProgress, setTextProgress] = useState(0);
  const [fullTextToAnimate, setFullTextToAnimate] = useState(''); // Store the complete text we're animating
  const [visibleCharCount, setVisibleCharCount] = useState(0); // How many characters are currently visible
  const [conversationStarted, setConversationStarted] = useState(false);
  const [userInput, setUserInput] = useState('');
  const [isListening, setIsListening] = useState(false);
  const [speechRecognition, setSpeechRecognition] = useState<SpeechRecognition | null>(null);
  const [continuousMicMode, setContinuousMicMode] = useState(false);
  const [userRoleProfile, setUserRoleProfile] = useState<'USER' | 'CAREGIVER' | 'HEALTHCARE_PROFESSIONAL' | 'PARTNER' | null>(null);
  const [showRoleModal, setShowRoleModal] = useState(true);

  // Map role values to display names
  const getRoleDisplayName = (role: 'USER' | 'CAREGIVER' | 'HEALTHCARE_PROFESSIONAL' | 'PARTNER'): string => {
    const roleNames = {
      'USER': 'Community Member',
      'CAREGIVER': 'Caregiver',
      'HEALTHCARE_PROFESSIONAL': 'Healthcare Professional',
      'PARTNER': 'Partner'
    };
    return roleNames[role];
  };

  // Handle role selection and auto-start conversation
  const handleRoleSelection = (role: 'USER' | 'CAREGIVER' | 'HEALTHCARE_PROFESSIONAL' | 'PARTNER') => {
    console.log('👤 Role selected:', role);
    setUserRoleProfile(role);
    setShowRoleModal(false);
    // The useEffect will automatically start the conversation once role is set
    if (onStartPoc) {
      onStartPoc();
    }
  };
  const [conversation, setConversation] = useState<Array<{role: 'user' | 'assistant', content: string}>>([]);
  const [currentResponseText, setCurrentResponseText] = useState<string>('');
  const [currentUserQuestion, setCurrentUserQuestion] = useState<string>('');
  const currentUserQuestionRef = useRef<string>('');
  const [isFirstSpeech, setIsFirstSpeech] = useState(true);
  const shouldRestartMicRef = useRef<boolean>(false);
  const [greetingTrigger, setGreetingTrigger] = useState(0);
  const [conversationSession, setConversationSession] = useState<{
    websocket: WebSocket;
    sendMessage: (text: string) => void;
    close: () => void;
  } | null>(null);
  const [audioContextEnabled, setAudioContextEnabled] = useState(false);
  const [temperature, setTemperature] = useState(0.7); // Default to Creative
  const audioContextEnabledRef = useRef(false);
  const currentAudioSourceRef = useRef<AudioBufferSourceNode | null>(null);
  const audioChunksRef = useRef<Uint8Array[]>([]);
  const isReceivingAudioRef = useRef(false);
  const audioAnalyserRef = useRef<AnalyserNode | null>(null);
  const audioAnalyserDataRef = useRef<Uint8Array | null>(null);
  const [isPaused, setIsPaused] = useState(false);
  const pauseTimeRef = useRef<number>(0);
  const audioBufferRef = useRef<AudioBuffer | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const textAnimationIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const audioStartTimeRef = useRef<number>(0);
  const textCharIndexRef = useRef<number>(0);
  const isManuallyPausingRef = useRef<boolean>(false);

  // Immediately try to enable AudioContext
  if (!audioContextEnabledRef.current) {
    try {
      const testContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      testContext.close();
      audioContextEnabledRef.current = true;
      setAudioContextEnabled(true);
    } catch (error) {
      console.error('❌ Failed to create test AudioContext:', error);
    }
  }

  // Initialize with empty text - agent will provide responses
  useEffect(() => {
    setCurrentResponseText('');
  }, []);

  // Start conversation when avatar is loaded and autoPlay is true - but only after role is selected
  useEffect(() => {
    if (avatarLoaded && !hasInitialized && autoPlay && !conversationSession && !isLoading && userRoleProfile) {
      console.log('🎯 Auto-starting conversation with role:', userRoleProfile);
      setHasInitialized(true);
      startConversationWithAgent();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [avatarLoaded, autoPlay, hasInitialized, conversationSession, isLoading, userRoleProfile]);

  // Enable AudioContext immediately when component mounts (user already clicked START POC)
  useEffect(() => {
    const enableAudioContext = async () => {
      if (!audioContextEnabled) {
        try {
          const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();

          if (audioContext.state === 'suspended') {
            await audioContext.resume();
          }

          audioContext.close(); // Just testing, we'll create new ones as needed
          setAudioContextEnabled(true);
        } catch (error) {
          console.error('❌ Failed to enable AudioContext:', error);
        }
      }
    };

    // Run immediately
    enableAudioContext();
  }, []); // Empty dependency array to run only on mount

  // Enable AudioContext on additional user interaction (backup)
  const enableAudioContext = useCallback(async () => {
    if (!audioContextEnabled) {
      try {
        const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
        await audioContext.resume();
        audioContext.close(); // Just testing, we'll create new ones as needed
        setAudioContextEnabled(true);
      } catch (error) {
        console.error('❌ Failed to enable AudioContext:', error);
      }
    }
  }, [audioContextEnabled]);

  const [elevenLabsService] = useState(() => new ElevenLabsService({
    apiKey,
    voiceId,
    agentId,
  }));


  // Function to split text into sentences for sliding window display
  const splitIntoSentences = useCallback((text: string): string[] => {
    if (!text) return [];

    // Split by sentence endings (., !, ?) but keep the punctuation
    const sentences = text.match(/[^\.!?]*[\.!?]/g) || [text];

    return sentences.map(s => s.trim()).filter(s => s.length > 0);
  }, []);

  // Function to simulate real-time text display synchronized with speech
  const startTextAnimation = useCallback((textToShow: string, resumeFromIndex: number = 0) => {
    // Store the full text
    setFullTextToAnimate(textToShow);

    // Only reset if starting fresh (not resuming)
    if (resumeFromIndex === 0) {
      setVisibleCharCount(0);
      setTextProgress(0);
    } else {
      setVisibleCharCount(resumeFromIndex);
    }

    // Calculate timing based on speech rate (approximately 180 words per minute)
    const wordsPerMinute = 180;
    const charactersPerMinute = wordsPerMinute * 5;
    const charactersPerSecond = charactersPerMinute / 60;
    const msPerCharacter = 1000 / charactersPerSecond;

    let currentIndex = resumeFromIndex;
    const interval = setInterval(() => {
      if (currentIndex < textToShow.length) {
        currentIndex++;
        setVisibleCharCount(currentIndex);
        setTextProgress((currentIndex / textToShow.length) * 100);
        textCharIndexRef.current = currentIndex;
      } else {
        clearInterval(interval);
        setTextProgress(100);
      }
    }, msPerCharacter);

    // Store the interval reference
    textAnimationIntervalRef.current = interval;

    return interval;
  }, []);

  const playBufferedAudio = useCallback(async () => {
    if (audioChunksRef.current.length === 0) return;

    // Concatenate all audio chunks into one continuous stream
    const totalLength = audioChunksRef.current.reduce((sum, chunk) => sum + chunk.length, 0);
    const combinedAudio = new Uint8Array(totalLength);
    let offset = 0;
    for (const chunk of audioChunksRef.current) {
      combinedAudio.set(chunk, offset);
      offset += chunk.length;
    }

    // Audio processing parameters for ElevenLabs
    const sampleRate = 16000; // ElevenLabs uses 16000 Hz for conversational agents
    const numChannels = 1; // Mono
    const bitsPerSample = 16; // 16-bit PCM

    if (audioContextEnabledRef.current || audioContextEnabled) {
      try {
        const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();

        // Resume AudioContext if suspended
        if (audioContext.state === 'suspended') {
          await audioContext.resume();
        }

        // Convert raw PCM data to AudioBuffer
        const audioBuffer = audioContext.createBuffer(numChannels, combinedAudio.length / 2, sampleRate);
        const channelData = audioBuffer.getChannelData(0);

        // Convert 16-bit PCM to float32 for Web Audio API (little-endian)
        for (let i = 0; i < channelData.length; i++) {
          const sample = (combinedAudio[i * 2] | (combinedAudio[i * 2 + 1] << 8));
          // Convert from signed 16-bit to float32 (-1.0 to 1.0)
          channelData[i] = sample < 32768 ? sample / 32768 : (sample - 65536) / 32768;
        }

        // Store audio buffer and context for pause/resume
        audioBufferRef.current = audioBuffer;
        audioContextRef.current = audioContext;

        // Stop any currently playing audio to prevent overlapping
        if (currentAudioSourceRef.current) {
          try {
            currentAudioSourceRef.current.stop();
          } catch (e) {
            // Audio might already be stopped
          }
        }

        // Create analyser for viseme detection
        const analyser = audioContext.createAnalyser();
        analyser.fftSize = 512;
        analyser.smoothingTimeConstant = 0.3; // Faster response
        analyser.minDecibels = -90;
        analyser.maxDecibels = -10;

        const bufferLength = analyser.frequencyBinCount;
        const dataArray = new Uint8Array(bufferLength);

        // Set up audio chain: source -> analyser -> destination
        const source = audioContext.createBufferSource();
        source.buffer = audioBuffer;
        source.connect(analyser);
        analyser.connect(audioContext.destination);

        // Store references for viseme detection
        audioAnalyserRef.current = analyser;
        audioAnalyserDataRef.current = dataArray;
        currentAudioSourceRef.current = source;


        // Create a promise to handle the audio playback
        const audioPromise = new Promise<void>((resolve, reject) => {
          source.onended = () => {
            currentAudioSourceRef.current = null;
            // Clear analyser references when audio ends
            audioAnalyserRef.current = null;
            audioAnalyserDataRef.current = null;
            resolve();
          };

          try {
            audioStartTimeRef.current = audioContext.currentTime;
            pauseTimeRef.current = 0; // Reset pause time when starting new audio
            source.start(0);
          } catch (startError) {
            reject(startError);
          }
        });

        // Clear any existing audio URL since we're using Web Audio API
        if (audioUrl) {
          elevenLabsService.revokeAudioUrl(audioUrl);
          setAudioUrl(null);
        }

        // Trigger animations
        setGreetingTrigger(Date.now());
        setIsFirstSpeech(false);
        setIsPlaying(true);
        setIsPaused(false);
        textCharIndexRef.current = 0;
        setVisibleCharCount(0);
        setFullTextToAnimate(''); // Will be set by startTextAnimation

        // Handle the end of playback
        audioPromise.then(() => {
          if (!isManuallyPausingRef.current) {
            setIsPlaying(false);
            setIsPaused(false);
          }
        }).catch((playError) => {
          console.error('❌ Web Audio playback failed:', playError);
          setIsPlaying(false);
          setIsPaused(false);
        });


      } catch (webAudioError) {
        console.error('❌ Web Audio API failed:', webAudioError);
        setIsPlaying(false);
      }
    } else {
    }
  }, []);

  const handlePauseResume = useCallback(() => {
    if (isPaused) {
      // Resume playback
      setIsPaused(false);
      setIsPlaying(true);

      // Resume Web Audio API playback if we have the audio buffer
      if (audioBufferRef.current && audioContextRef.current && pauseTimeRef.current > 0) {
        const audioContext = audioContextRef.current;
        const audioBuffer = audioBufferRef.current;

        // Create a new source from the stored buffer
        const analyser = audioContext.createAnalyser();
        analyser.fftSize = 512;
        analyser.smoothingTimeConstant = 0.3;
        analyser.minDecibels = -90;
        analyser.maxDecibels = -10;

        const bufferLength = analyser.frequencyBinCount;
        const dataArray = new Uint8Array(bufferLength);

        const source = audioContext.createBufferSource();
        source.buffer = audioBuffer;
        source.connect(analyser);
        analyser.connect(audioContext.destination);

        // Store references for viseme detection
        audioAnalyserRef.current = analyser;
        audioAnalyserDataRef.current = dataArray;
        currentAudioSourceRef.current = source;

        // Start from the paused position
        const offset = pauseTimeRef.current;
        audioStartTimeRef.current = audioContext.currentTime;
        source.start(0, offset);

        // Handle end of playback
        source.onended = () => {
          currentAudioSourceRef.current = null;
          audioAnalyserRef.current = null;
          audioAnalyserDataRef.current = null;

          // Only reset isPaused if this wasn't a manual pause
          if (!isManuallyPausingRef.current) {
            setIsPlaying(false);
            setIsPaused(false);
            pauseTimeRef.current = 0;
          }
        };
      }
    } else {
      // Pause playback
      // Set flag to indicate we're manually pausing (not naturally ending)
      isManuallyPausingRef.current = true;

      setIsPaused(true);
      setIsPlaying(false);

      // Clear the text animation interval to freeze text at current position
      if (textAnimationIntervalRef.current) {
        clearInterval(textAnimationIntervalRef.current);
        textAnimationIntervalRef.current = null;
      }

      // For Web Audio API: stop the source and save the current time
      if (currentAudioSourceRef.current && audioContextRef.current) {
        const currentTime = audioContextRef.current.currentTime;
        const elapsedTime = currentTime - audioStartTimeRef.current;
        pauseTimeRef.current = pauseTimeRef.current + elapsedTime;

        try {
          currentAudioSourceRef.current.stop();
        } catch (e) {
          console.log('Audio source already stopped');
        }
      }

      // Reset the flag after a brief delay (after onended has been called)
      setTimeout(() => {
        isManuallyPausingRef.current = false;
      }, 100);
    }
  }, [isPaused, isPlaying, visibleCharCount]);

  const startConversationWithAgent = useCallback(async () => {
    console.log('🎬 START: startConversationWithAgent called with role:', userRoleProfile);

    if (!apiKey || apiKey === 'your-api-key-here' || !apiKey.startsWith('sk_')) {
      setError(`Valid Eleven Labs API key is required. Current: ${apiKey ? apiKey.substring(0, 10) + '...' : 'undefined'}`);
      return;
    }

    // Prevent multiple simultaneous calls
    if (isLoading) {
      console.log('⏸️ Already loading, skipping duplicate call');
      return;
    }
    setIsLoading(true);
    setError(null);
    setIsPlaying(false);

    try {
      // Try to create conversation session, but don't fail if it doesn't work
      console.log('🔗 Creating session with role:', userRoleProfile);
      const session = await elevenLabsService.createConversationSession(undefined, userRoleProfile).catch(error => {
        console.warn('⚠️ Conversation session failed, will use fallback TTS:', error);
        return null;
      });

      if (session) {

        // Set up event handlers for the WebSocket
        session.websocket.onmessage = async (event) => {
        const data = JSON.parse(event.data);

        // Log the full message for debugging (including pings to see frequency)
        if (data.type !== 'audio' && data.type !== 'ping') {
          console.log('📨 WebSocket message type:', data.type, data);
        }

        switch (data.type) {
          case 'ping':
            // Respond to ping with pong
            if (data.ping_event) {
              session.websocket.send(JSON.stringify({
                type: 'pong',
                event_id: data.ping_event.event_id
              }));
            }
            break;

          case 'agent_response':
            if (data.agent_response_event && data.agent_response_event.agent_response) {
              setIsLoading(false); // Stop loading when we get a response
              setCurrentResponseText(data.agent_response_event.agent_response);
              setConversation(prev => [...prev, {
                role: 'assistant',
                content: data.agent_response_event.agent_response
              }]);

              // Track the complete user interaction for analytics
              const storedQuestion = currentUserQuestionRef.current;
              console.log('🔍 Analytics Debug - storedQuestion from ref:', storedQuestion);
              console.log('🔍 Analytics Debug - agentResponse:', data.agent_response_event.agent_response);
              if (storedQuestion) {
                console.log('📊 Tracking user interaction for analytics');
                analyticsService.trackUserInteraction({
                  userQuestion: storedQuestion,
                  agentResponse: data.agent_response_event.agent_response,
                  agentId: agentId
                });
                currentUserQuestionRef.current = ''; // Clear the stored question
                setCurrentUserQuestion(''); // Also clear state for consistency
              } else {
                console.log('❌ No storedQuestion - skipping analytics tracking');
              }

              // Agent response marks end of audio streaming - play buffered audio
              // Use setTimeout to avoid closure issues with playBufferedAudio
              if (audioChunksRef.current.length > 0) {
                setTimeout(async () => {
                  await playBufferedAudio();
                  audioChunksRef.current = []; // Clear buffer after playing
                  isReceivingAudioRef.current = false;
                }, 0);
              }
            }
            break;

          case 'audio':
            if (data.audio_event && data.audio_event.audio_base_64) {
              // Stop loading when we start receiving audio chunks
              if (isLoading) {
                setIsLoading(false);
                // If we don't have response text yet, set a placeholder for the welcome message
                if (!currentResponseText) {
                  setCurrentResponseText(welcomeMessage);
                }
              }

              isReceivingAudioRef.current = true;
              const audioData = atob(data.audio_event.audio_base_64);
              const audioArray = new Uint8Array(audioData.length);
              for (let i = 0; i < audioData.length; i++) {
                audioArray[i] = audioData.charCodeAt(i);
              }
              audioChunksRef.current.push(audioArray);
            }
            break;

          case 'user_transcript':
            // This might be the agent echoing back what we said
            break;

          case 'conversation_initiation_metadata':
            // Automatically send welcome message trigger to agent
            setTimeout(() => {
              if (session.websocket.readyState === WebSocket.OPEN) {
                const greetingText = `[RUNTIME]\nrole_profile=${userRoleProfile}\n\nPlease greet the user with: "${welcomeMessage}"`;
                const message = {
                  user_input: {
                    type: 'text',
                    text: greetingText
                  }
                };
                session.websocket.send(JSON.stringify(message));
              }
            }, 500); // Small delay to ensure connection is fully ready
            break;

          case 'interruption':
            setIsPlaying(false);
            break;

          default:

            // Check ALL possible fields that might contain a response
            const possibleResponseFields = [
              'agent_response', 'text', 'message', 'response',
              'transcript', 'content', 'reply', 'answer',
              'agent_text', 'assistant_response', 'bot_response'
            ];

            for (const field of possibleResponseFields) {
              if (data[field]) {
              }
            }

            // Also check nested structures
            if (data.data) {
            }
            if (data.event) {
            }
        }
      };

      session.websocket.onerror = (error) => {
        console.error('❌ WebSocket error:', error);
        setError('Connection to agent failed');
        setIsLoading(false);
      };

      session.websocket.onclose = () => {
        setConversationSession(null);
      };

        setConversationSession(session);
        setConversationStarted(true);
      } else {
        // No conversation session, but still mark as started for fallback TTS
        setConversationStarted(true);
      }

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to start conversation with agent';
      console.error('❌ Conversation setup failed:', errorMessage);
      setError(errorMessage);
      setIsPlaying(false);

      // Show error for longer
      setTimeout(() => {
        if (error === errorMessage) {
          setError(null);
        }
      }, 5000);
    } finally {
      // Don't set isLoading(false) here - let it be controlled by agent response or audio start
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiKey, elevenLabsService, userRoleProfile]);

  const handleAvatarLoaded = useCallback(() => {
    setAvatarLoaded(true);
  }, []);

  const handleAudioEnd = useCallback(() => {
    setIsPlaying(false);
    setIsPaused(false);
    pauseTimeRef.current = 0;
    textCharIndexRef.current = 0;
    // Complete the text display when audio ends (full text)
    setCurrentText(currentResponseText);
    setDisplayedText(currentResponseText);
    setFullTextToAnimate('');
    setVisibleCharCount(0);
    setTextProgress(100);
  }, [currentResponseText, isPlaying]);

  // Handle beta signup form opening - let Ellie continue speaking
  const handleBetaSignupFormOpen = useCallback(() => {
  }, []);

  // Handle beta signup completion - make Ellie thank the user
  const handleBetaSignupComplete = useCallback((firstName: string) => {

    if (conversationSession && conversationSession.websocket.readyState === WebSocket.OPEN) {
      const thankYouText = `[RUNTIME]\nrole_profile=${userRoleProfile}\n\nThank ${firstName} for signing up for the beta! Express your excitement and let them know they'll be among the first to experience the new features.`;
      const thankYouMessage = {
        user_input: {
          type: 'text',
          text: thankYouText
        }
      };

      setTimeout(() => {
        if (conversationSession.websocket.readyState === WebSocket.OPEN) {
          conversationSession.websocket.send(JSON.stringify(thankYouMessage));
        }
      }, 500);
    } else {
      // Fallback: could show a visual thank you message or trigger a new conversation
    }
  }, [conversationSession, userRoleProfile]);

  // Function to send user message to agent
  const generateResponse = useCallback(async (userMessage: string) => {

    if (!userMessage.trim()) {
      return;
    }

    setIsLoading(true);
    setError(null);

    // Add user message to conversation history
    setConversation(prev => [...prev, {role: 'user' as const, content: userMessage}]);

    // Store the current user question for analytics tracking
    setCurrentUserQuestion(userMessage);
    currentUserQuestionRef.current = userMessage;
    console.log('💾 Stored user question for analytics:', userMessage);

    if (conversationSession) {
      try {
        // Send message to agent via WebSocket
        conversationSession.sendMessage(userMessage);

        setUserInput(''); // Clear input, but keep loading state until agent responds

      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to send message to agent';
        console.error('🤔 DEBUG: Error sending WebSocket message:', errorMessage);
        setError(errorMessage);
        setIsLoading(false);
        setUserInput('');
      }

    } else {
      // Fallback to direct TTS with a generic response
      try {
        const fallbackResponse = `I received your message: "${userMessage}". I'm a virtual assistant here to help you with The Hub Central. How can I assist you further?`;

        setCurrentResponseText(fallbackResponse);
        setConversation(prev => [...prev, {
          role: 'assistant',
          content: fallbackResponse
        }]);

        // Track the interaction for analytics (fallback case)
        analyticsService.trackUserInteraction({
          userQuestion: userMessage,
          agentResponse: fallbackResponse,
          agentId: agentId || 'fallback'
        });

        // Generate speech using direct TTS
        const audioBlob = await elevenLabsService.generateSpeech({
          text: fallbackResponse,
          voice_settings: {
            stability: 0.5,
            similarity_boost: 0.5,
          },
        });

        const url = elevenLabsService.createAudioUrl(audioBlob);
        setAudioUrl(url);

        // Trigger animations
        setGreetingTrigger(Date.now());
        setIsFirstSpeech(false);
        setIsPlaying(true);
        setIsPaused(false);
        textCharIndexRef.current = 0;
        setVisibleCharCount(0);
        setFullTextToAnimate(''); // Will be set by startTextAnimation

      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to generate fallback response';
        console.error('🤔 DEBUG: Error with fallback TTS:', errorMessage);
        setError(errorMessage);
      } finally {
        setIsLoading(false);
        setUserInput('');
      }
    }
  }, [conversationSession, elevenLabsService]);

  // Initialize speech recognition
  useEffect(() => {
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
      const recognition = new SpeechRecognition();

      recognition.continuous = false;
      recognition.interimResults = true;
      recognition.lang = 'en-US';

      recognition.onstart = () => {
        setIsListening(true);
      };

      recognition.onresult = (event: any) => {
        const transcript = event.results[0][0].transcript;
        setUserInput(transcript);

        // If the result is final, automatically send the message
        if (event.results[0].isFinal) {
          setTimeout(() => {
            if (transcript.trim()) {
              generateResponse(transcript);
            }
          }, 500);
        }
      };

      recognition.onerror = (event: any) => {
        console.error('🎤 Speech recognition error:', event.error);
        setIsListening(false);
      };

      recognition.onend = () => {
        console.log('🎤 Speech recognition ended');
        setIsListening(false);
      };

      setSpeechRecognition(recognition);
    }
  }, [generateResponse]);

  // Mute microphone when avatar starts speaking
  useEffect(() => {
    if (isPlaying && isListening && speechRecognition) {
      console.log('🔇 Muting microphone - avatar is speaking');
      try {
        speechRecognition.stop();
      } catch (err) {
        console.error('Error stopping speech recognition:', err);
      }
    }
  }, [isPlaying, isListening, speechRecognition]);

  // Handle continuous microphone mode - restart recognition after avatar finishes speaking
  useEffect(() => {
    if (continuousMicMode && speechRecognition && !isListening && !isPlaying && conversationStarted) {
      // Small delay before restarting to avoid rapid restarts and ensure avatar audio has finished
      const timer = setTimeout(() => {
        console.log('🎤 Restarting microphone - avatar finished speaking, continuous mode enabled');
        try {
          speechRecognition.start();
        } catch (err) {
          console.error('Error restarting speech recognition:', err);
        }
      }, 500);

      return () => clearTimeout(timer);
    }
  }, [continuousMicMode, speechRecognition, isListening, isPlaying, conversationStarted]);

  const handleSendMessage = () => {
    if (userInput.trim() && !isLoading) {
      generateResponse(userInput);
    } else {
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const toggleListening = () => {
    if (!speechRecognition) {
      setError('Speech recognition not supported in this browser');
      return;
    }

    if (isListening) {
      speechRecognition.stop();
    } else {
      setUserInput(''); // Clear previous input
      speechRecognition.start();
    }
  };

  const toggleContinuousMicMode = () => {
    const newMode = !continuousMicMode;
    setContinuousMicMode(newMode);

    if (newMode && speechRecognition && !isListening && conversationStarted) {
      // Start listening immediately when enabling continuous mode
      setUserInput('');
      speechRecognition.start();
    } else if (!newMode && isListening && speechRecognition) {
      // Stop listening when disabling continuous mode
      speechRecognition.stop();
    }
  };

  const handlePlayPause = () => {
    if (!conversationSession && !isLoading && !conversationStarted) {
      startConversationWithAgent();

      // Safety timeout - if nothing happens in 10 seconds, reset
      setTimeout(() => {
        if (isLoading) {
          setIsLoading(false);
          setError('Request timed out. Please try again.');
        }
      }, 10000);

    } else if (audioUrl) {
      setIsPlaying(!isPlaying);
    }
  };

  // Update currentText based on visibleCharCount
  useEffect(() => {
    if (fullTextToAnimate) {
      const newText = fullTextToAnimate.substring(0, visibleCharCount);
      setCurrentText(newText);
    }
  }, [visibleCharCount, fullTextToAnimate, isPaused, isPlaying]);

  // Start text animation when playing starts
  useEffect(() => {
    let textInterval: NodeJS.Timeout | null = null;

    if (isPlaying && currentResponseText && !isPaused) {
      // Resume from current position if we have one, otherwise start from beginning
      const startIndex = textCharIndexRef.current || 0;
      textInterval = startTextAnimation(currentResponseText, startIndex);
      // Set displayedText when we start playing so the box appears
      if (!displayedText || displayedText !== currentResponseText) {
        setDisplayedText(currentResponseText);
      }
    } else if (!isPlaying && !isPaused && currentResponseText) {
      // Only show full text when truly finished (not paused)
      setCurrentText(currentResponseText);
      setDisplayedText(currentResponseText);
      setFullTextToAnimate('');
      setVisibleCharCount(0);
      setTextProgress(100);
      textCharIndexRef.current = 0; // Reset for next response
    }
    // If paused, don't do anything - keep text frozen at current position

    return () => {
      if (textInterval) {
        clearInterval(textInterval);
      }
    };
  }, [isPlaying, isPaused, startTextAnimation, currentResponseText, displayedText]);

  useEffect(() => {
    return () => {
      if (audioUrl) {
        elevenLabsService.revokeAudioUrl(audioUrl);
      }
    };
  }, [audioUrl, elevenLabsService]);

  // Cleanup WebSocket connection on unmount
  useEffect(() => {
    return () => {
      if (conversationSession) {
        conversationSession.close();
      }
    };
  }, [conversationSession]);

  const handleStopConversation = useCallback(() => {
    // Close WebSocket connection
    if (conversationSession) {
      conversationSession.close();
      setConversationSession(null);
    }

    // Stop any playing audio
    if (currentAudioSourceRef.current) {
      try {
        currentAudioSourceRef.current.stop();
      } catch (e) {
        // Audio might already be stopped
      }
      currentAudioSourceRef.current = null;
    }

    // Clear audio analyser references
    audioAnalyserRef.current = null;
    audioAnalyserDataRef.current = null;

    // Reset audio chunks buffer
    audioChunksRef.current = [];
    isReceivingAudioRef.current = false;

    // Reset states
    setIsPlaying(false);
    setIsPaused(false);
    pauseTimeRef.current = 0;
    textCharIndexRef.current = 0;
    setIsLoading(false);
    setConversationStarted(false);
    setHasInitialized(false);  // Reset this so conversation can restart
    setCurrentResponseText('');
    setCurrentText('');
    setDisplayedText('');
    setFullTextToAnimate('');
    setVisibleCharCount(0);
    setTextProgress(0);

    // Clear audio URL if exists
    if (audioUrl) {
      elevenLabsService.revokeAudioUrl(audioUrl);
      setAudioUrl(null);
    }

    // Reset pocStarted state in parent component
    if (onStop) {
      onStop();
    }

  }, [conversationSession, audioUrl, elevenLabsService, onStop]);

  // Handle pronunciation testing
  const handleTestPronunciation = useCallback(async (text: string, label: string) => {
    console.log(`Testing pronunciation: ${label} - "${text}"`);

    try {
      setIsLoading(true);
      setError(null);

      // Generate speech using direct TTS
      const audioBlob = await elevenLabsService.generateSpeech({
        text: text,
        voice_settings: {
          stability: 0.5,
          similarity_boost: 0.5,
        },
      });

      const url = elevenLabsService.createAudioUrl(audioBlob);
      setAudioUrl(url);

      // Set the text to display
      setCurrentResponseText(text);
      setDisplayedText(text);

      // Trigger animations
      setGreetingTrigger(Date.now());
      setIsFirstSpeech(false);
      setIsPlaying(true);
      setIsPaused(false);
      textCharIndexRef.current = 0; // Reset text animation for new audio

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to test pronunciation';
      console.error('Error testing pronunciation:', errorMessage);
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [elevenLabsService]);

  return (
    <div
      style={{
        width: '100%',
        height: '100vh',
        display: 'flex',
        justifyContent: 'center',
        backgroundColor: '#1a1a1a'
      }}
      onClick={enableAudioContext}
    >
      <div style={{ 
        position: 'relative', 
        width: '1480px',
        maxWidth: '100vw',
        height: '100vh',
        backgroundImage: 'url(/background.png)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat'
      }}>
        <div style={{
          position: 'absolute',
          bottom: '0',
          right: '0',
          width: '592px',
          maxWidth: '40vw',
          height: '80%',
          zIndex: 1
        }}>
          <VRMAvatar
            key="vrm-avatar-stable"
            audioUrl={audioUrl || undefined}
            isPlaying={isPlaying}
            isPaused={isPaused}
            onLoadComplete={handleAvatarLoaded}
            onAudioEnd={handleAudioEnd}
            playGreeting={greetingTrigger > 0}
            greetingTrigger={greetingTrigger}
            externalAnalyser={audioAnalyserRef.current}
            externalAnalyserData={audioAnalyserDataRef.current}
          />
        </div>
        
        {/* Only show ControlPanel if role is selected - for error handling and stop button */}
        {userRoleProfile && (
          <ControlPanel
            error={error}
            isLoading={isLoading}
            avatarLoaded={avatarLoaded}
            conversationStarted={conversationStarted}
            isPlaying={isPlaying}
            onPlayPause={handlePlayPause}
            onRetry={startConversationWithAgent}
            onClearError={() => setError(null)}
            onStartPoc={onStartPoc}
            onStop={handleStopConversation}
            pocStarted={pocStarted}
            userRoleName={getRoleDisplayName(userRoleProfile)}
          />
        )}

        {/* User Role Profile Modal */}
        {showRoleModal && !conversationStarted && (
          <>
            {/* Modal Overlay */}
            <div style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0, 0, 0, 0.7)',
              backdropFilter: 'blur(5px)',
              zIndex: 9999,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              animation: 'fadeIn 0.3s ease-in-out'
            }}>
              {/* Modal Content */}
              <div style={{
                background: 'white',
                padding: '40px',
                borderRadius: '20px',
                boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
                maxWidth: '500px',
                width: '90%',
                animation: 'slideUp 0.3s ease-out'
              }}>
                <div style={{
                  fontSize: '24px',
                  fontWeight: '700',
                  color: '#2c3e50',
                  marginBottom: '12px',
                  fontFamily: 'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
                  textAlign: 'center'
                }}>
                  Welcome to The Hub Central
                </div>
                <div style={{
                  fontSize: '14px',
                  color: '#7f8c8d',
                  marginBottom: '30px',
                  fontFamily: 'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
                  textAlign: 'center',
                  lineHeight: '1.5'
                }}>
                  Please select your role to personalize your experience
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {[
                    { value: 'USER', label: 'Community Member', description: 'Looking for support and resources', icon: '👤' },
                    { value: 'CAREGIVER', label: 'Caregiver', description: 'Supporting someone at home', icon: '💚' },
                    { value: 'HEALTHCARE_PROFESSIONAL', label: 'Healthcare Professional', description: 'Integrating into clinical workflow', icon: '⚕️' },
                    { value: 'PARTNER', label: 'Partner', description: 'Offering services on the platform', icon: '💼' }
                  ].map((role) => (
                    <button
                      key={role.value}
                      onClick={() => handleRoleSelection(role.value as 'USER' | 'CAREGIVER' | 'HEALTHCARE_PROFESSIONAL' | 'PARTNER')}
                      style={{
                        padding: '20px',
                        borderRadius: '12px',
                        border: '2px solid #e0e0e0',
                        background: 'white',
                        cursor: 'pointer',
                        textAlign: 'left',
                        transition: 'all 0.2s ease',
                        fontFamily: 'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '15px'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = '#f8f9fa';
                        e.currentTarget.style.borderColor = '#4ecdc4';
                        e.currentTarget.style.transform = 'translateY(-2px)';
                        e.currentTarget.style.boxShadow = '0 4px 12px rgba(78, 205, 196, 0.2)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'white';
                        e.currentTarget.style.borderColor = '#e0e0e0';
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = 'none';
                      }}
                    >
                      <div style={{ fontSize: '32px' }}>
                        {role.icon}
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{
                          fontSize: '16px',
                          fontWeight: '600',
                          color: '#2c3e50',
                          marginBottom: '4px'
                        }}>
                          {role.label}
                        </div>
                        <div style={{
                          fontSize: '13px',
                          color: '#7f8c8d',
                          lineHeight: '1.4'
                        }}>
                          {role.description}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            </div>
            {/* Add CSS animations */}
            <style>{`
              @keyframes fadeIn {
                from { opacity: 0; }
                to { opacity: 1; }
              }
              @keyframes slideUp {
                from {
                  opacity: 0;
                  transform: translateY(30px);
                }
                to {
                  opacity: 1;
                  transform: translateY(0);
                }
              }
            `}</style>
          </>
        )}

        {/* Beta Signup Button */}
        <div style={{
          position: 'absolute',
          top: '20px',
          right: '20px',
          zIndex: 10001,
          background: 'rgba(0, 0, 0, 0.8)',
          padding: '15px',
          borderRadius: '10px',
          maxWidth: '250px'
        }}>
          <TemperatureControl
            onBetaSignupComplete={handleBetaSignupComplete}
            onBetaSignupFormOpen={handleBetaSignupFormOpen}
            isOpenMode={agentId === 'agent_9601k5fc9k76etvb6eaed2r44fzk'}
          />

          {/* Visit Web Button */}
          <a
            href="https://thehubcentral.com/"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px',
              marginTop: '8px',
              padding: '8px 16px',
              background: '#ff6b6b',
              color: 'white',
              textDecoration: 'none',
              borderRadius: '20px',
              fontSize: '12px',
              fontWeight: '600',
              fontFamily: 'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
              transition: 'all 0.2s',
              cursor: 'pointer',
              width: '100%',
              boxSizing: 'border-box'
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.background = '#ff5252';
              e.currentTarget.style.transform = 'translateY(-1px)';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.background = '#ff6b6b';
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            <span>🌐</span>
            <span>Visit Web</span>
          </a>
        </div>

        {/* Pronunciation Tester - Hidden for now */}
        {/* <div style={{
          position: 'absolute',
          top: '150px',
          left: '20px',
          zIndex: 1000
        }}>
          <PronunciationTester
            onTestPronunciation={handleTestPronunciation}
            isDisabled={isLoading || isPlaying}
          />
        </div> */}

        {/* Real-time speech text container */}
        <div style={{
          position: 'absolute',
          bottom: '30px',
          left: '50%',
          transform: 'translateX(-50%)',
          display: 'flex',
          flexDirection: 'column',
          gap: '15px',
          maxWidth: '70%',
          minWidth: '500px',
          zIndex: 1000,
        }}>
          {/* Avatar response text */}
          {conversationStarted && (displayedText || fullTextToAnimate) && (
            <div style={{
              position: 'relative',
              background: 'white',
              padding: '25px 35px',
              borderRadius: '20px',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12), 0 2px 8px rgba(0, 0, 0, 0.08)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              backdropFilter: 'blur(10px)',
            }}>
              <TextDisplay
                currentText={isPlaying || isPaused ? currentText : displayedText}
                textProgress={textProgress}
                isPlaying={isPlaying || isPaused}
                isLoading={isLoading}
                audioUrl={audioUrl}
                conversationStarted={conversationStarted}
              />

              {/* Pause/Resume button - only show when avatar is speaking or paused */}
              {(isPlaying || isPaused) && !isLoading && (
                <button
                  onClick={handlePauseResume}
                  style={{
                    position: 'absolute',
                    top: '15px',
                    right: '15px',
                    background: isPaused ? '#4ecdc4' : '#95a5a6',
                    border: 'none',
                    borderRadius: '50%',
                    width: '45px',
                    height: '45px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '20px',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                    transition: 'all 0.2s ease',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'scale(1.1)';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.25)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'scale(1)';
                    e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.15)';
                  }}
                  title={isPaused ? 'Resume' : 'Pause'}
                >
                  {isPaused ? '▶️' : '⏸️'}
                </button>
              )}
            </div>
          )}

          {/* User input or status messages */}
          <div style={{
            background: 'white',
            padding: '25px 35px',
            borderRadius: '20px',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12), 0 2px 8px rgba(0, 0, 0, 0.08)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            backdropFilter: 'blur(10px)',
          }}>
            {conversationStarted && isLoading ? (
              <div style={{
                fontSize: '18px',
                color: '#4ecdc4',
                fontStyle: 'italic',
                textAlign: 'center',
                fontFamily: 'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
              }}>
                ⏳ Generating speech...
              </div>
            ) : conversationStarted && !isPlaying && !isLoading ? (
              <ConversationInput
                userInput={userInput}
                setUserInput={setUserInput}
                isLoading={isLoading}
                isListening={isListening}
                speechRecognition={speechRecognition}
                continuousMicMode={continuousMicMode}
                onSendMessage={handleSendMessage}
                onToggleListening={toggleListening}
                onToggleContinuousMic={toggleContinuousMicMode}
                onKeyPress={handleKeyPress}
              />
            ) : isPlaying || isPaused ? (
              <div style={{
                fontSize: '18px',
                color: '#4ecdc4',
                fontStyle: 'italic',
                textAlign: 'center',
                fontFamily: 'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
              }}>
                {isPaused ? '⏸️ Paused - Click ▶️ to continue' : '🎤 Ellie is speaking...'}
              </div>
            ) : (
              <div style={{
                fontSize: '18px',
                color: '#7f8c8d',
                fontStyle: 'italic',
                textAlign: 'center',
                fontFamily: 'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
              }}>
                {isLoading ? (
                  <span style={{ color: '#4ecdc4' }}>⏳ Generating speech...</span>
                ) : audioUrl && !conversationStarted ? (
                  'Click the Start button to begin!'
                ) : (
                  'Click Start to meet Ellie'
                )}
              </div>
            )}
          </div>
        </div>

        {/* Add CSS for blinking cursor */}
        <style>{`
          @keyframes blink {
            0%, 50% { opacity: 1; }
            51%, 100% { opacity: 0; }
          }
        `}</style>
      </div>
    </div>
  );
};

export default WelcomeAvatar;